import { ChevronDown, ChevronRight, History } from '@orgus/ui/icons';
import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { RUN_STATUS_LABEL, clip } from '@orgus/protocol';
import { Badge, Button, IconButton, Pressable, RowStatus, Tooltip, WorkStateIcon, cn, formatClock, formatCompact, formatDuration, formatInt, plural, runRowStatus, shortPath } from '@orgus/ui';
import type { WorkState } from '@orgus/ui';
import { NO_PROJECT, PROJECT_LANES, attemptNote, groupByProject, lanesShown, phaseGroups, placement, ticksFor, workflowAgents, workflowProgress } from './model.ts';
import type { Lane, PhaseGroup, TimelineRun } from './model.ts';
import { activeMs, barPieces, bursts, marksIn, summarize, summarizeRuns, unionActivity } from './activity.ts';
import { ActivityTip, BarPieces, burstsText, countsText, labelSlot } from './ActivityBar.tsx';
import type { PieceTone } from './ActivityBar.tsx';
import { plainText } from '../../lib/plainText.ts';

/**
 * Bar colours. Only the tones a person may act on carry colour: running (working) and failed
 * (danger). Done and stopped are quiet grey with no edge (status.md rule 1: done is neutral in
 * lists), so the live bars are what the eye finds.
 */
type BarTone = 'working' | 'danger' | 'neutral';

const BAR: Record<BarTone, PieceTone & { text: string }> = {
  working: { fill: 'bg-status-working-soft', edge: 'border-status-working-line', solid: 'bg-status-working', text: 'text-status-working-ink' },
  danger: { fill: 'bg-status-danger-soft', edge: 'border-status-danger-line', solid: 'bg-status-danger', text: 'text-status-danger-ink' },
  neutral: { fill: 'bg-active', edge: 'border-transparent', solid: 'bg-fg-subtle', text: 'text-fg-subtle' },
};

function barTone(r: Pick<TimelineRun, 'status'>): BarTone {
  return r.status === 'running' ? 'working' : r.status === 'failed' ? 'danger' : 'neutral';
}

/** Agents shown per phase before "Show all". */
const PHASE_PREVIEW = 40;

const KIND_LABEL: Record<TimelineRun['kind'], string> = { subagent: 'Subagent', workflow: 'Workflow', agent: 'Workflow agent' };

/**
 * The label column, shared by the axis, the grid lines and every lane so they line up. Below
 * 768 px there is no track: the timeline is a list, and the label column is the whole row.
 */
const LABEL_W = 'min-w-0 flex-1 md:w-60 md:flex-none lg:w-72 2xl:w-80';
/** The track: hidden on a phone, where a bar would be a sliver. */
const TRACK = 'relative mr-4 hidden h-full min-w-0 flex-1 md:block';
/**
 * A lane's numeric columns: on a phone its time (no bar shows it), beside the track its tool
 * calls. A phone leaves the tool calls to the drawer, so the name keeps the room.
 */
const TIME_COL = 'w-12 shrink-0 text-right text-caption text-fg-subtle num md:hidden';
const TOOLS_COL = 'hidden w-10 shrink-0 text-right text-caption text-fg-subtle num md:block';

export interface TimelineProps {
  lanes: Lane[];
  start: number;
  end: number;
  step: number;
  machineName: (id: string) => string;
  projectName: (id: string | null) => string | null;
  /** 'stale' or 'offline' when the session's machine is not reporting: its live bars are not live. */
  machineState?: (id: string) => 'online' | 'stale' | 'offline';
  onOpen: (run: TimelineRun) => void;
  activeRunId?: string | null;
  /** Workflows open into their agents unless toggled: by default when this says so. */
  defaultOpen?: (run: TimelineRun) => boolean;
}

/** Roughly one caption character, in px, for deciding whether a label fits. */
const CHAR_PX = 7;

/** Whole minutes once past a minute: a bar label, not a stopwatch. */
function barDuration(ms: number): string {
  if (ms < 60_000) return formatDuration(ms);
  return formatDuration(Math.floor(ms / 60_000) * 60_000);
}

/** Measures an element's width (for deciding what fits inside a bar). */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/**
 * One lane: the label column and the track, at one height (`h-lane`) for every kind of lane
 * (spacing-layout.md: one row height per list, group headers included).
 */
function LaneRow({ label, track, header, className }: { label: ReactNode; track?: ReactNode; header?: boolean; className?: string }) {
  return (
    <div data-row={header ? undefined : ''} data-group-header={header ? '' : undefined} className={cn('flex h-lane items-center', className)}>
      <div className={cn('flex h-full shrink-0 items-center gap-2', LABEL_W)}>{label}</div>
      <div className={TRACK}>{track}</div>
    </div>
  );
}

/**
 * A lane's label, set in by its depth: one 24 px group step per level (GROUP_INDENT), so each
 * lane's icon sits under its parent's icon plus one step, and a fold chevron hangs in the step
 * before the icon (under the parent's icon), as in every grouped list.
 */
function Indented({ depth, className, children }: { depth: number; className?: string; children: ReactNode }) {
  return (
    <span className="flex min-w-0 flex-1 items-center pl-row-x pr-3">
      {/* Half a step per level on a phone, where the name needs the room. */}
      {Array.from({ length: depth }, (_, k) => (
        <span key={k} className="w-3 shrink-0 md:w-6" aria-hidden />
      ))}
      <span className={cn('flex min-w-0 flex-1 items-center gap-2', className)}>{children}</span>
    </span>
  );
}

/** The fold chevron of a lane: a 24 px button centred on the 16 px chevron slot, so what follows starts one step in. */
function FoldButton({ label, open, onClick }: { label: string; open: boolean; onClick: () => void }) {
  return <IconButton size="xs" className="-mx-1" label={label} icon={<ChevronRight className={cn('transition-transform duration-(--duration-fast)', open && 'rotate-90')} />} aria-expanded={open} onClick={onClick} noTooltip />;
}

/** A hairline across the track: a session's or a phase's span. */
function Span({ from, to, start, end, live }: { from: number; to: number | null; start: number; end: number; live: boolean }) {
  const p = placement({ startedAt: from, endedAt: to }, start, end);
  if (p.width <= 0) return null;
  return (
    <span
      className={cn('absolute top-1/2 h-px -translate-y-1/2', live ? 'bg-border-strong' : 'bg-border')}
      style={{ left: `${p.left * 100}%`, width: `${p.width * 100}%`, transition: 'left 1s linear, width 1s linear' }}
      aria-hidden
    />
  );
}

/**
 * A Gantt of runs, project by project: an axis, then per project a header lane that folds, per
 * session a header lane and one lane per run. A workflow opens into its agents, grouped by
 * phase, one lane each.
 */
export function Timeline({ lanes, start, end, step, machineName, projectName, machineState, onOpen, activeRunId, defaultOpen }: TimelineProps) {
  const [trackRef, trackW] = useWidth<HTMLDivElement>();
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const span = end - start;
  const ticks = ticksFor(start, end, step);
  const x = (t: number) => `${((t - start) / span) * 100}%`;
  // Keep tick labels at least 64 px apart on narrow tracks.
  const labelEvery = trackW > 0 && ticks.length ? Math.max(1, Math.ceil(64 / (trackW / ticks.length))) : 1;
  // A label needs room for half its width before the track's start, and for the Now marker after it.
  const px = (fraction: number) => fraction * (trackW > 0 ? trackW : 1000);
  const labelFits = (t: number) => px((end - t) / span) > 88 && px((t - start) / span) > 32;
  const isOpen = (r: TimelineRun) =>
    toggled[r.id] ?? ((defaultOpen?.(r) ?? r.status === 'running') || (!!activeRunId && !!r.agents?.some((a) => a.id === activeRunId)));
  const machines = new Set(lanes.map((l) => l.session.machineId));
  const groups = groupByProject(lanes);
  const [folded, setFolded] = useState<Record<string, boolean>>({});
  // Projects whose every session is shown (past PROJECT_LANES).
  const [every, setEvery] = useState<Record<string, boolean>>({});

  return (
    <div className="relative" data-list="">
      {/* Axis: sticky while the lanes scroll. */}
      <div className="sticky top-0 z-(--z-sticky) flex h-row items-center bg-surface">
        <div className={cn('flex shrink-0 items-center justify-between gap-3 pl-row-x pr-3 text-caption font-medium text-fg-subtle', LABEL_W)}>
          <span>Session and run</span>
          <span className="flex items-center gap-2">
            <span className={TIME_COL}>Time</span>
            <span className={cn(TOOLS_COL, 'font-medium')} title="Tool calls">
              Tools
            </span>
          </span>
        </div>
        <div ref={trackRef} className={TRACK} aria-hidden>
          {ticks.map((t, i) =>
            i % labelEvery === 0 && labelFits(t) ? (
              <span key={t} className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-caption text-fg-subtle num" style={{ left: x(t) }}>
                {formatClock(t)}
              </span>
            ) : null,
          )}
          {/* Ends on the now line, never past the track's edge. */}
          <span className="absolute right-0 top-1/2 -translate-y-1/2 pr-1 text-caption font-medium text-accent-text">Now</span>
        </div>
      </div>

      <div className="relative">
        {/* Grid lines and the now line, behind the bars. */}
        <div className="pointer-events-none absolute inset-0 hidden md:flex" aria-hidden>
          <span className={cn('shrink-0', LABEL_W)} />
          <span className="relative mr-4 flex-1">
            {ticks.map((t) => (
              <span key={t} className="absolute inset-y-0 w-px bg-border-subtle" style={{ left: x(t) }} />
            ))}
            <span className="absolute inset-y-0 right-0 w-px bg-accent-line" />
          </span>
        </div>

        <ul className="relative" aria-label="Runs by project">
          {groups.map((g) => {
            const name = g.key === NO_PROJECT ? 'No project' : (projectName(g.key) ?? 'Unknown project');
            const shown = !folded[g.key];
            const laneList = shown ? lanesShown(g.lanes, !!every[g.key], activeRunId ?? null) : [];
            const rest = g.lanes.length - laneList.length;
            return (
              <li key={g.key} className="pb-3 last:pb-1">
                <ProjectLane name={name} sessions={g.lanes.length} running={g.running} open={shown} onToggle={() => setFolded((f) => ({ ...f, [g.key]: shown }))} />
                {shown && (
                  <ul aria-label={`Sessions in ${name}`}>
                    {laneList.map((lane) => {
                      const live = (machineState?.(lane.session.machineId) ?? 'online') === 'online';
                      // The project is said once, in its header: a lane names its machine only when there are several.
                      const where = machines.size > 1 ? machineName(lane.session.machineId) : null;
                      return (
                        <li key={lane.session.id} className="motion-arrive pt-1">
                          <SessionLane lane={lane} start={start} end={end} where={where} notReporting={!live} />
                          <ul aria-label={`Runs in ${lane.session.title}`}>
                            {lane.runs.map((r) => {
                              const open = !!r.agents?.length && isOpen(r);
                              return (
                                <li key={r.id}>
                                  <RunLane
                                    run={r}
                                    start={start}
                                    end={end}
                                    trackW={trackW}
                                    onOpen={onOpen}
                                    active={r.id === activeRunId}
                                    live={live}
                                    open={open}
                                    onToggle={r.agents?.length ? () => setToggled((t) => ({ ...t, [r.id]: !open })) : undefined}
                                  />
                                  {open && <AgentLanes run={r} start={start} end={end} trackW={trackW} onOpen={onOpen} activeRunId={activeRunId ?? null} live={live} />}
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {/* A quiet fold, like the Overview's Needs you: the project opens in place. */}
                {shown && g.lanes.length > PROJECT_LANES && (rest > 0 || every[g.key]) && (
                  <Pressable
                    onClick={() => setEvery((e) => ({ ...e, [g.key]: !e[g.key] }))}
                    aria-expanded={!!every[g.key]}
                    className="my-1 flex w-auto items-center gap-1 rounded-control pl-row-x text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
                  >
                    {every[g.key] ? 'Show fewer sessions' : `Show ${formatInt(rest)} more ${rest === 1 ? 'session' : 'sessions'}`}
                    <ChevronDown className={cn('size-3 transition-transform duration-(--duration-fast)', every[g.key] && 'rotate-180')} aria-hidden />
                  </Pressable>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/**
 * A project's header lane: a fold control, the name, and its sessions and running agents
 * counted, quietly, on a faint band across the whole width (one lane height, like every lane).
 */
function ProjectLane({ name, sessions, running, open, onToggle }: { name: string; sessions: number; running: number; open: boolean; onToggle: () => void }) {
  return (
    <div data-group-header="" className="flex h-lane items-center gap-2 rounded-control bg-hover pl-row-x pr-4">
      <FoldButton label={open ? `Hide the sessions of ${name}` : `Show the sessions of ${name}`} open={open} onClick={onToggle} />
      <span className="hidden shrink-0 text-caption text-fg-subtle sm:inline">Project</span>
      <span className="min-w-0 truncate text-ui font-medium text-fg" title={name}>
        {name}
      </span>
      <span className="ml-auto hidden shrink-0 whitespace-nowrap text-caption text-fg-subtle num sm:inline">
        {plural(sessions, 'session')}
        {running ? `, ${formatCompact(running)} running` : ''}
      </span>
      {/* On a phone the name keeps the room: only the running count, or the sessions when none run. */}
      <span className="ml-auto shrink-0 whitespace-nowrap text-caption text-fg-subtle num sm:hidden">
        {running ? `${formatCompact(running)} running` : plural(sessions, 'session')}
      </span>
    </div>
  );
}

/** The session's header lane: status, title, where, and its runs counted. */
const SESSION_STATE: Record<Lane['session']['status'], WorkState> = { waiting: 'needs-you', working: 'running', idle: 'stale', ended: 'stale' };

function SessionLane({ lane, start, end, where, notReporting }: { lane: Lane; start: number; end: number; where: string | null; notReporting: boolean }) {
  const s = lane.session;
  const live = s.status !== 'ended';
  const sum = summarizeRuns(lane.runs, end);
  return (
    <LaneRow
      header
      label={
        <Indented depth={1}>
          <WorkStateIcon state={SESSION_STATE[s.status]} />
          <span className="min-w-0 truncate text-ui text-fg" title={s.title}>
            {s.title}
          </span>
        </Indented>
      }
      track={
        <>
          <Span from={s.startedAt} to={live ? null : s.lastActivity} start={start} end={end} live={live} />
          {/* Fixed columns, so the machine and the counts sit at the same place on every session lane. */}
          <span className="absolute inset-y-0 right-0 flex items-center gap-4 whitespace-nowrap bg-surface pl-3 text-caption text-fg-subtle num">
            {notReporting && lane.running > 0 ? (
              <Badge tone="waiting" size="sm" data-chip="">
                Not reporting: last known state
              </Badge>
            ) : (
              where && (
                <span data-meta="" className="hidden w-36 min-w-0 items-center gap-1 lg:flex">
                  <span className="shrink-0">Machine</span>
                  <span className="min-w-0 truncate font-mono text-fg-muted" title={where}>
                    {where}
                  </span>
                </span>
              )
            )}
            <span data-meta="" className="w-48 truncate text-right" title={countsText(sum)}>
              {countsText(sum)}
            </span>
          </span>
        </>
      }
    />
  );
}

function RunLane({
  run,
  start,
  end,
  trackW,
  onOpen,
  active,
  live,
  open,
  onToggle,
}: {
  run: TimelineRun;
  start: number;
  end: number;
  trackW: number;
  onOpen: (r: TimelineRun) => void;
  active: boolean;
  live: boolean;
  open: boolean;
  onToggle?: () => void;
}) {
  const flow = run.kind === 'workflow';
  return (
    <LaneRow
      label={
        <Indented depth={1}>
          {onToggle ? (
            <FoldButton label={open ? `Hide the agents of ${run.label}` : `Show the ${plural(run.agents?.length ?? 0, 'agent')} of ${run.label}`} open={open} onClick={onToggle} />
          ) : (
            <span className="w-4 shrink-0" aria-hidden />
          )}
          <RowStatus {...runRowStatus(run.status)} hideLabel focusable={false} />
          <Tooltip content={run.title}>
            <Pressable onClick={() => onOpen(run)} className="-mx-1 min-w-0 flex-1 truncate rounded-control px-1 text-left text-ui text-fg-muted transition-colors duration-(--duration-fast) hover:bg-hover hover:text-fg">
              {flow ? run.label : run.title}
            </Pressable>
          </Tooltip>
          <span className={TIME_COL}>{barDuration((run.endedAt ?? end) - run.startedAt)}</span>
          {/* Tool calls only: a workflow's progress is on its bar and in its phase lanes. */}
          <span className={TOOLS_COL} title={run.toolCalls != null ? plural(run.toolCalls, 'tool call') : undefined}>
            {run.toolCalls != null ? formatCompact(run.toolCalls) : ''}
          </span>
        </Indented>
      }
      track={<RunBar run={run} start={start} end={end} trackW={trackW} onOpen={onOpen} active={active} live={live} />}
    />
  );
}

/** A workflow's agents: per phase a header lane, then one lane per agent. */
function AgentLanes({
  run,
  start,
  end,
  trackW,
  onOpen,
  activeRunId,
  live,
}: {
  run: TimelineRun;
  start: number;
  end: number;
  trackW: number;
  onOpen: (r: TimelineRun) => void;
  activeRunId: string | null;
  live: boolean;
}) {
  const groups = phaseGroups(run);
  return (
    <ul aria-label={`Agents of ${run.label}`}>
      {groups.map((g) => (
        <li key={g.title || '(no phase)'}>
          <PhaseLanes group={g} start={start} end={end} onOpen={onOpen} activeRunId={activeRunId} trackW={trackW} live={live} />
        </li>
      ))}
      {run.agentsOmitted > 0 && (
        <li>
          <LaneRow
            label={
              <Indented depth={3}>
                <span className="truncate text-caption text-fg-subtle">
                  {plural(run.agentsOmitted, 'more agent')} not listed (the machine reports up to {formatCompact(run.agents?.length ?? 0)})
                </span>
              </Indented>
            }
          />
        </li>
      )}
    </ul>
  );
}

function PhaseLanes({
  group,
  start,
  end,
  onOpen,
  activeRunId,
  trackW,
  live,
}: {
  group: PhaseGroup;
  start: number;
  end: number;
  onOpen: (r: TimelineRun) => void;
  activeRunId: string | null;
  trackW: number;
  live: boolean;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? group.agents : group.agents.slice(0, PHASE_PREVIEW);
  // Failures lead, so the one count that matters is never the one cut off.
  const facts = [group.failed ? `${group.failed} failed` : null, group.running ? `${group.running} running` : null, group.done ? `${group.done} done` : null].filter(Boolean).join(', ') || plural(group.agents.length, 'agent');
  const name = group.title || 'No phase';
  return (
    <>
      {/* The phase is a sub-group: its facts sit in its own lane, never on a row of their own. */}
      <LaneRow
        header
        label={
          <Indented depth={3} className="text-caption">
            <span className="shrink-0 font-medium text-fg-muted">{name}</span>
            <span className="min-w-0 truncate text-fg-subtle num">{facts}</span>
          </Indented>
        }
        track={<Span from={group.startedAt} to={group.endedAt} start={start} end={end} live={group.endedAt == null} />}
      />
      <ul aria-label={`${name}: ${facts}`}>
        {shown.map((a) => (
          <li key={a.id}>
            <AgentLane agent={a} start={start} end={end} trackW={trackW} onOpen={onOpen} active={a.id === activeRunId} live={live} />
          </li>
        ))}
      </ul>
      {group.agents.length > shown.length && (
        <LaneRow
          label={
            <Indented depth={3}>
              {/* The label, not the button's edge, starts in the agents' column. */}
              <Button variant="ghost" size="sm" className="-ml-2" onClick={() => setAll(true)}>
                Show all {formatCompact(group.agents.length)} in {name}
              </Button>
            </Indented>
          }
        />
      )}
    </>
  );
}

/** An agent's attempt as a small glyph; its words ("Earlier attempt") on hover and for screen readers. */
export function AttemptMark({ note }: { note: string }) {
  const text = `${note.charAt(0).toUpperCase()}${note.slice(1)}`;
  return (
    <Tooltip content={text}>
      <span data-chip="" className="flex size-4 shrink-0 items-center justify-center text-fg-subtle">
        <History className="size-3.5" aria-hidden />
        <span className="sr-only">{text}</span>
      </span>
    </Tooltip>
  );
}

function AgentLane({ agent, start, end, trackW, onOpen, active, live }: { agent: TimelineRun; start: number; end: number; trackW: number; onOpen: (r: TimelineRun) => void; active: boolean; live: boolean }) {
  const note = attemptNote(agent);
  return (
    <LaneRow
      label={
        <Indented depth={3}>
          <RowStatus {...runRowStatus(agent.status)} hideLabel focusable={false} />
          <span className={cn('min-w-0 truncate text-ui', agent.supersededBy ? 'text-fg-subtle' : 'text-fg-muted')} title={agent.label}>
            {agent.label}
          </span>
          {/* The attempt is a glyph with its words on hover: the name keeps the room. */}
          {note && <AttemptMark note={note} />}
          <span className={cn(TIME_COL, 'ml-auto')}>{barDuration((agent.endedAt ?? end) - agent.startedAt)}</span>
          <span className={cn(TOOLS_COL, 'ml-auto')} title={plural(agent.toolCalls ?? 0, 'tool call')}>
            {formatCompact(agent.toolCalls ?? 0)}
          </span>
        </Indented>
      }
      track={<RunBar run={agent} start={start} end={end} trackW={trackW} onOpen={onOpen} active={active} live={live} slim />}
    />
  );
}

function RunBar({
  run,
  start,
  end,
  trackW,
  onOpen,
  active,
  live,
  slim,
}: {
  run: TimelineRun;
  start: number;
  end: number;
  trackW: number;
  onOpen: (r: TimelineRun) => void;
  active: boolean;
  live: boolean;
  slim?: boolean;
}) {
  const shaped = shapeRun(run, end);
  const pieces = barPieces(shaped, end);
  const marks = marksIn(shaped, start, end);
  const split = pieces.length > 1 || marks.length > 0;
  const from = pieces[0]?.from ?? run.startedAt;
  const to = pieces[pieces.length - 1]?.to ?? run.endedAt ?? end;
  const p = placement({ startedAt: from, endedAt: to }, start, end);
  const px = p.width * trackW;
  const c = BAR[barTone(run)];
  const running = run.status === 'running';
  const dur = (run.endedAt ?? end) - run.startedAt;
  const act = activeMs(shaped, end);
  const fullDur = split && act.measured ? `${barDuration(act.ms)} active` : barDuration(dur);
  const burstText = split ? burstsText(shaped, end) : null;
  const style: CSSProperties = {
    // A bar is at least 8 px wide and never runs past the now line.
    left: `min(${p.left * 100}%, calc(100% - 8px))`,
    width: `max(${p.width * 100}%, 8px)`,
    transition: 'left 1s linear, width 1s linear',
  };
  // A bar with gaps carries its label inside its widest burst, clear of the mark glyphs.
  const cFrom = Math.max(from, start);
  const widest = split ? labelSlot(pieces, marks, cFrom, to, px) : undefined;
  const labelPx = split ? (widest?.w ?? 0) * px : px;
  const showLabel = !slim && labelPx > 128;
  const showDur = labelPx > 62;
  // Never cut the duration mid-word: "1h 07m active" becomes "1h 07m" when the bar is short.
  const shownDur = fullDur !== barDuration(dur) && labelPx < fullDur.length * CHAR_PX + 24 ? barDuration(act.ms) : fullDur;
  const segments = run.kind === 'workflow' && run.status === 'running' && run.phases && !run.agents?.length;
  const note = attemptNote(run);
  const name = run.kind === 'workflow' ? run.label : run.title;
  // A workflow's progress, from all its agents whatever the filter lists: "4 of 8".
  const all = run.kind === 'workflow' ? workflowAgents(run) : [];
  const sum = all.length ? summarize(all, end) : null;
  const progress = sum ? `${formatCompact(sum.finished)} of ${formatCompact(sum.total + run.agentsOmitted)}` : null;
  const showProgress = progress != null && labelPx > 128 + progress.length * CHAR_PX + 16;

  return (
    <Tooltip content={<RunTip run={run} end={end} />} side="top" delay={150}>
      <Pressable
        onClick={() => onOpen(run)}
        data-run-id={run.id}
        // The visible text (name, duration) leads the accessible name (WCAG 2.5.3, label in name).
        aria-label={`${showDur && !slim ? `${showLabel ? `${name} ${showProgress ? `${progress} ` : ''}` : ''}${shownDur}, ` : ''}${KIND_LABEL[run.kind]} ${name}${note ? ` (${note})` : ''}, ${RUN_STATUS_LABEL[run.status]}${progress ? `, ${progress} agents finished` : ''}, ${burstText ?? formatDuration(dur)}${marks.length ? `, ${plural(marks.length, 'mark')}` : ''}. Open details`}
        style={style}
        className={cn(
          'absolute top-1/2 w-auto -translate-y-1/2 rounded-control',
          slim ? 'h-3' : 'h-5',
          'transition-shadow duration-(--duration-fast) hover:shadow-elevation-1 focus-visible:outline-offset-1',
          split ? 'overflow-visible' : cn('overflow-hidden border', c.fill, c.edge, p.clipped && 'rounded-l-none border-l-0'),
          active && 'ring-2 ring-focus ring-offset-1 ring-offset-surface',
        )}
      >
        {split ? (
          <BarPieces pieces={pieces} marks={marks} from={cFrom} to={to} px={px} tone={c} live={live} running={running && pieces[pieces.length - 1]?.kind === 'active'} />
        ) : segments ? (
          <PhaseSegments run={run} tone={c} />
        ) : run.kind === 'workflow' && run.agents?.length ? (
          <AgentShare run={run} />
        ) : null}
        {showDur && !slim && (
          <span
            className={cn('pointer-events-none flex h-full items-center gap-2 text-caption font-medium num', widest ? 'absolute inset-y-0 overflow-hidden pl-3 pr-2' : 'relative px-2', c.text)}
            style={widest ? { left: `${widest.l * 100}%`, width: `${widest.w * 100}%` } : undefined}
          >
            {showLabel && <span className="min-w-0 truncate">{name}</span>}
            {showLabel && ' '}
            {showLabel && showProgress && <span className="shrink-0 font-normal">{progress}</span>}
            {showLabel && showProgress && ' '}
            <span className="shrink-0">{shownDur}</span>
          </span>
        )}
        {/* The sheen: a band of light crosses a running bar (the indeterminate stripe, M11); under reduced motion it is not drawn at all. */}
        {running && !split && !slim && live && <span data-sheen="" className="pointer-events-none absolute inset-y-0 left-0 w-2/5 animate-stripe bg-linear-to-r from-transparent via-fg/15 to-transparent motion-reduce:hidden" aria-hidden />}
        {/* The grow caret (M2): a static 2 px bar at the leading edge of a running span. It never blinks. */}
        {running && !split && <span data-caret="" className="absolute inset-y-0 right-0 w-0.5 bg-fg-muted" aria-hidden />}
      </Pressable>
    </Tooltip>
  );
}

/**
 * A workflow bar filled by the share of its agents that finished (grey) and failed (red).
 * Stopped agents count as finished: a stopped workflow is a full bar, never an empty one.
 */
function AgentShare({ run }: { run: TimelineRun }) {
  // The whole workflow's share, never the filtered lanes': a running workflow listed for its one
  // failed agent stays a running bar with a small failed share, not a full red one.
  const units = workflowAgents(run);
  const n = units.length || 1;
  const done = units.filter((a) => a.status === 'done' || a.status === 'stopped').length / n;
  const failed = units.filter((a) => a.status === 'failed').length / n;
  return (
    <span className="absolute inset-0 flex" aria-hidden>
      {/* Fills move by transform, never width (no layout per update). */}
      <span className="absolute inset-0 origin-left bg-fg-faint opacity-40 transition-transform duration-(--duration-slow) ease-settle" style={{ transform: `scaleX(${done})` }} />
      <span className="absolute inset-0 origin-left bg-status-danger opacity-40 transition-transform duration-(--duration-slow) ease-settle" style={{ transform: `translateX(${done * 100}%) scaleX(${failed})` }} />
    </span>
  );
}

/** Workflow phases as segments sized by agent count, each filled by its finished share. */
function PhaseSegments({ run, tone }: { run: TimelineRun; tone: PieceTone }) {
  const phases = run.phases ?? [];
  return (
    <span className="absolute inset-0 flex gap-px" aria-hidden>
      {phases.map((ph, i) => {
        // A phase no agent has entered is empty, never a full (finished-looking) segment.
        const frac = ph.agents > 0 ? Math.min(1, ph.done / ph.agents) : 0;
        return (
          <span key={`${ph.title}-${i}`} className="relative h-full min-w-1 basis-0" style={{ flexGrow: Math.max(1, ph.agents) }}>
            <span className={cn('absolute inset-0 origin-left opacity-40 transition-transform duration-(--duration-slow) ease-settle', tone.solid)} style={{ transform: `scaleX(${frac})` }} />
          </span>
        );
      })}
    </span>
  );
}

/** A workflow's bar draws the union of its agents' bursts. */
function shapeRun(run: TimelineRun, now: number): TimelineRun {
  const agents = run.kind === 'workflow' ? workflowAgents(run) : [];
  return agents.length ? { ...run, activity: unionActivity(agents, now) } : run;
}

/**
 * A bar's tooltip: what it is, then a few labelled facts (Started, Running for or Took, Active,
 * Tools) instead of prose that says the length twice, then its marks, folded. The rest is in the drawer.
 */
function RunTip({ run, end }: { run: TimelineRun; end: number }) {
  const dur = (run.endedAt ?? end) - run.startedAt;
  const shaped = shapeRun(run, end);
  const act = activeMs(shaped, end);
  const n = act.measured ? bursts(shaped, end).length : 0;
  const facts: [string, string][] = [
    ['Started', formatClock(run.startedAt)],
    [run.endedAt ? 'Took' : 'Running for', formatDuration(dur)],
    ...(act.measured ? [['Active', n > 1 ? `${formatDuration(act.ms)} in ${formatCompact(n)} bursts` : formatDuration(act.ms)] as [string, string]] : []),
    ...(run.toolCalls != null ? [['Tools', formatCompact(run.toolCalls)] as [string, string]] : []),
  ];
  return (
    <div className="flex min-w-0 max-w-72 flex-col gap-2 py-1">
      <span className="flex min-w-0 flex-col">
        <span className="text-caption text-fg-subtle">
          {KIND_LABEL[run.kind]}, {run.kind === 'agent' ? run.phase || 'no phase' : run.label}, {RUN_STATUS_LABEL[run.status].toLowerCase()}
        </span>
        <span className="font-medium text-fg">{run.title}</span>
      </span>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-caption">
        {facts.map(([k, v]) => (
          <div key={k} className="flex min-w-0 flex-col">
            <dt className="text-fg-subtle">{k}</dt>
            <dd className="whitespace-nowrap text-fg-muted num">{v}</dd>
          </div>
        ))}
      </dl>
      {run.phases && (
        <span className="text-caption text-fg-muted">
          {run.phases.map((p) => `${p.title} ${p.done}/${p.agents}`).join(', ')} ({Math.round(workflowProgress(run.phases) * 100)}%)
        </span>
      )}
      <ActivityTip run={shaped} />
      {run.worktree && <span className="truncate text-caption text-fg-subtle">Worktree {shortPath(run.worktree, 40)}</span>}
      {run.lastText && <span className="text-caption text-fg-muted">{clip(plainText(run.lastText), 140)}</span>}
    </div>
  );
}
